import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import daynight from 'daynight';
import { defaultPrefs } from '../utils/defaults';
import {
  fetchUser,
  getCurrentUser,
  getMisc,
  loadFromLocalForage,
  pushPrefs,
  updateMisc,
  updateUser,
  upgrade,
} from '../utils/books';
import type { Prefs, SnackbarMessage, UserInfo } from '../types/book';
import throttle from 'lodash-es/throttle';
import { eventsUrl } from '../config';
import { dispatchServerEvent } from '../utils/friends';
import { currentAppVersion } from '../utils/appVersion';
import { logger } from '../utils/logger';
import { checkVersion } from '../utils/api';
import { useOnlineStore } from '../store/onlineStore';
import { getAppPlatform, isNativeApp, openExternalUrl, openNativeAndroidStoreUpdate } from '../utils/platform';
import {
  type AppVersionInfo,
  beginPendingAppUpdate,
  clearPendingAppUpdate,
  compareAppReleaseVersion,
  compareAppVersionInfo,
  compareNativeAppVersionInfo,
  dismissAppUpdatePrompt,
  formatAppVersionLabel,
  getAppVersionInfoFromMisc,
  getPendingAppUpdate,
  isAppUpdatePromptDismissed,
  MAX_APP_UPDATE_AUTO_RELOAD_ATTEMPTS,
  resolvePendingAppUpdate,
  withAppVersionInMisc,
} from '../utils/versioning';
import { requestServiceWorkerUpdate } from '../utils/pwaRegistration';

interface AppContextType {
  prefs: Prefs | undefined;
  changePrefs: (p: Partial<Prefs>) => void;
  effectiveTheme: 'light' | 'dark' | undefined;
  snackbarMessage: SnackbarMessage | null;
  setSnackbarMessage: (m: SnackbarMessage) => void;
  isOnline: boolean | null;
  setOnline: (o: boolean) => void;
  checkAppVersion: () => void;
  localForageLoaded: boolean;
  user: UserInfo | null | undefined;
  setUser: (u: UserInfo | null | undefined) => void;
  refreshId: number;
  triggerRefresh: () => void;
}

export const AppContext = createContext<AppContextType>({
  prefs: undefined,
  changePrefs: () => {},
  effectiveTheme: undefined,
  snackbarMessage: null,
  setSnackbarMessage: () => {},
  isOnline: true,
  setOnline: () => {},
  checkAppVersion: () => {},
  localForageLoaded: false,
  user: undefined,
  setUser: () => {},
  refreshId: 0,
  triggerRefresh: () => {},
});

type NativeUpdateTarget = AppVersionInfo & {
  forceUpgrade?: boolean;
  storeUrl?: string | null;
  storeUpdatePromptsEnabled?: boolean;
};

const shouldPresentNativeStoreUpdatePrompt = (targetVersionInfo: NativeUpdateTarget): boolean =>
  Boolean(targetVersionInfo.forceUpgrade || targetVersionInfo.storeUpdatePromptsEnabled);

const DEFAULT_VERSION_CHECK_INTERVAL_MS = 15 * 60 * 1000;
const NATIVE_VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const PENDING_UPDATE_CHECK_INTERVAL_MS = 60 * 1000;

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [prefs, setPrefs] = useState<Prefs>();
  const [effectiveTheme, setEffectiveTheme] = useState<'light' | 'dark' | undefined>();
  const [snackbarMessage, setSnackbarMessage] = useState<SnackbarMessage>(null);
  const { isOnline, setOnline } = useOnlineStore();
  const [localForageLoaded, setLocalForageLoaded] = useState(false);
  const [user, setUser] = useState<UserInfo | null | undefined>();
  const [refreshId, setRefreshId] = useState(0);
  const hasReconciledVersionStateRef = useRef(false);
  const versionCheckInFlightRef = useRef(false);
  const reloadTimeoutRef = useRef<number | null>(null);

  const triggerRefresh = () => {
    setRefreshId((id) => id + 1);
    const latestUser = getCurrentUser();
    if (latestUser) {
      setUser(latestUser);
      setPrefs(latestUser.prefs);
      logger.debug('Refreshed app preference state from updated local user cache', {
        prefsPresent: Boolean(latestUser.prefs),
        groupCount: Object.keys(latestUser.prefs?.groups || {}).length,
        theme: latestUser.prefs?.theme || null,
      });
    }
  };

  useEffect(() => {
    return () => {
      if (reloadTimeoutRef.current !== null) {
        window.clearTimeout(reloadTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const startup = async () => {
      if (!localForageLoaded) {
        const loaded = await loadFromLocalForage();
        setLocalForageLoaded(loaded);
      }
    };
    startup();
  }, []);

  useEffect(() => {
    const updateUser = async () => {
      const u = await fetchUser(true);
      if (u !== undefined) {
        setUser(u);
      }
      const p = u?.prefs || defaultPrefs;
      setPrefs(p);
      //   setEffectiveTheme(
      //     p?.theme === 'light' ? 'light' : p?.theme === 'dark' ? 'dark' : daynight().light ? 'light' : 'dark'
      //   );
      // updateEffectiveTheme();
      const driveSync = (u as (UserInfo & { driveSync?: { enabled?: boolean; connectedAt?: number | null } }) | undefined)
        ?.driveSync;
      logger.debug('Startup remote user fetch finished', {
        userPresent: Boolean(u),
        emailPresent: Boolean(u?.email),
        driveSyncConfigured: Boolean(driveSync?.enabled),
        driveSyncConnectedAt: driveSync?.connectedAt || null,
        prefsPresent: Boolean(p),
        prefsTheme: p?.theme || null,
        prefsLanguageCode: p?.languageCode || null,
      });
      //   } else {
      // console.debug('startup remote fetch failed');
      //   }
    };
    if (localForageLoaded) {
      updateUser();
    }
  }, [localForageLoaded]);

  useEffect(() => {
    if (!localForageLoaded || user === undefined) {
      return;
    }

    void upgrade().catch((error) => {
      logger.error('Background book upgrade failed', { error });
    });
  }, [localForageLoaded, user?.email]);

  const updateEffectiveTheme = (newPrefs?: Prefs) => {
    console.log('updateEffectiveTheme, localForageLoaded =', localForageLoaded, ' prefs =', prefs);
    const theme = newPrefs?.theme || (localForageLoaded && prefs?.theme);
    if (theme) {
      if (theme === 'auto') {
        setEffectiveTheme(daynight().light ? 'light' : 'dark');
      } else if (theme === 'light' || theme === 'dark') {
        setEffectiveTheme(theme);
      }
    }
  };

  useEffect(() => {
    // Push a new entry onto the history stack upon initial load
    const handleLogout = (_event: CustomEvent): void => {
      logger.debug('detected logout in AppContext');
      //   setUser(null);
    };

    window.addEventListener('logout', handleLogout as EventListener);

    return () => window.removeEventListener('logout', handleLogout as EventListener);
  }, []);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    intervalId = setInterval(updateEffectiveTheme, 15 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [updateEffectiveTheme, prefs?.theme]);

  const throttledPushPrefs = throttle(pushPrefs, 5000);

  const changePrefs = (p: Partial<Prefs>) => {
    const u = updateUser({}, p);
    if (u) {
      setUser(u);
      setPrefs(u.prefs);
      updateEffectiveTheme(u.prefs);
      throttledPushPrefs();
    }
  };

  useEffect(() => {
    if (localForageLoaded && prefs?.theme && effectiveTheme !== prefs?.theme) {
      updateEffectiveTheme();
    }
  }, [prefs?.theme, localForageLoaded, updateEffectiveTheme]);

  //   const connect = useCallback(() => {
  //     const eventSource = new EventSource(eventsUrl);
  //     eventSource.onmessage = (event) => {
  //       console.log('event:', event);
  //       try {
  //         const data = JSON.parse(event.data);
  //         if (data.type === 'broadcast') {
  //           setSnackbarMessage({ text: data.text, duration: data.duration || 5000 });
  //         }
  //         setOnline(true);
  //       } catch (error) {
  //         console.error('Error parsing SSE message:', error);
  //       }
  //     };

  //     eventSource.onerror = (error) => {
  //       console.error('EventSource error:', error);
  //       eventSource.close();
  //       //   setSnackbarMessage({ text: 'Could not connect to server. It may be down for maintenance.', duration: null });
  //     };

  //     return eventSource;
  //   }, [eventsUrl, isOnline, setOnline]);

  //   useEffect(() => {
  //     if (!isOnline) {
  //       return;
  //     }

  //     let eventSource: EventSource | null = null;

  //     eventSource = connect();

  //     return () => {
  //       eventSource?.close();
  //     };
  //   }, [isOnline, connect]);

  useEffect(() => {
    if (!user?.email || !isOnline) {
      return;
    }

    logger.info('[realtime] opening event stream');
    const eventSource = new EventSource(eventsUrl);
    eventSource.onopen = () => {
      logger.debug('[realtime] event stream open');
    };
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'broadcast') {
          setSnackbarMessage({ text: data.text, duration: data.duration || 5000 });
        }
        if (data.type === 'book_notes_changed' || data.type === 'reader_presence' || data.type === 'reader_presence_left') {
          logger.debug('[realtime] event received', {
            type: data.type,
            owner: data.owner,
            email: data.email,
            hash: data.hash,
            changedNoteIds: data.changedNoteIds,
          });
        }
        dispatchServerEvent(data);
        setOnline(true);
      } catch (error) {
        logger.warn('failed to parse realtime event', { error, data: event.data });
      }
    };
    eventSource.onerror = (error) => {
      logger.debug('[realtime] event stream error', {
        readyState: eventSource.readyState,
        error,
      });
    };

    return () => {
      logger.debug('[realtime] closing event stream');
      eventSource.close();
    };
  }, [isOnline, setOnline, user?.email]);

  const persistCurrentAppVersion = useCallback(() => {
    updateMisc(withAppVersionInMisc(getMisc(), currentAppVersion));
  }, []);

  const openNativeStoreForUpdate = useCallback(
    async (
      targetVersionInfo: NativeUpdateTarget,
      reason: 'manual-action' | 'automatic-force-upgrade',
    ): Promise<boolean> => {
      const nativePlatform = getAppPlatform();
      logger.debug('[native-update] attempting to open store listing', {
        reason,
        currentVersion: currentAppVersion.displayVersion,
        targetVersion: targetVersionInfo.displayVersion,
        platform: nativePlatform,
        storeUrl: targetVersionInfo.storeUrl ?? '(none)',
      });

      if (nativePlatform === 'android') {
        const openResult = await openNativeAndroidStoreUpdate(targetVersionInfo.storeUrl);
        logger.debug('[native-update] Android store open attempt finished', {
          reason,
          currentVersion: currentAppVersion.displayVersion,
          targetVersion: targetVersionInfo.displayVersion,
          storeUrl: targetVersionInfo.storeUrl ?? '(none)',
          opened: openResult.opened,
          openedUrl: openResult.url ?? '(none)',
        });
        return openResult.opened;
      }

      if (!targetVersionInfo.storeUrl) {
        logger.warn('No native store URL is available for non-Android update prompt', {
          currentVersion: currentAppVersion.displayVersion,
          targetVersion: targetVersionInfo.displayVersion,
          platform: nativePlatform,
        });
        return false;
      }

      await openExternalUrl(targetVersionInfo.storeUrl);
      logger.debug('[native-update] Opened non-Android store URL', {
        reason,
        currentVersion: currentAppVersion.displayVersion,
        targetVersion: targetVersionInfo.displayVersion,
        platform: nativePlatform,
        storeUrl: targetVersionInfo.storeUrl,
      });
      return true;
    },
    [],
  );

  const presentNativeUpdatePrompt = useCallback(
    async (
      targetVersionInfo: NativeUpdateTarget,
      options?: {
        autoOpen?: boolean;
        reason?: 'fresh-check' | 'startup-restore' | 'visibility-restore';
      },
    ): Promise<void> => {
      const autoOpen = Boolean(options?.autoOpen);
      const reason = options?.reason ?? 'fresh-check';
      const targetVersion = targetVersionInfo.displayVersion;

      if (!shouldPresentNativeStoreUpdatePrompt(targetVersionInfo)) {
        clearPendingAppUpdate();
        logger.info('[native-update] suppressed native store update prompt', {
          reason,
          autoOpen,
          currentVersion: currentAppVersion.displayVersion,
          targetVersion,
          forceUpgrade: Boolean(targetVersionInfo.forceUpgrade),
          storeUpdatePromptsEnabled: Boolean(targetVersionInfo.storeUpdatePromptsEnabled),
          storeUrl: targetVersionInfo.storeUrl ?? '(none)',
        });
        return;
      }

      const targetVersionLabel = formatAppVersionLabel(targetVersionInfo);
      const promptText =
        autoOpen && targetVersionInfo.forceUpgrade
          ? `${targetVersionLabel} is required. Opening the store update page...`
          : targetVersionInfo.forceUpgrade
            ? `${targetVersionLabel} is required. Tap Update to open the store.`
            : `${targetVersionLabel} is available. Tap Update to open the store.`;
      const failedAutoOpenText = targetVersionInfo.forceUpgrade
        ? `${targetVersionLabel} is required, but the store could not be opened automatically. Tap Update to try again.`
        : `${targetVersionLabel} is available, but the store could not be opened automatically. Tap Update to try again.`;

      logger.debug('[native-update] presenting native update prompt', {
        reason,
        autoOpen,
        currentVersion: currentAppVersion.displayVersion,
        targetVersion,
        forceUpgrade: Boolean(targetVersionInfo.forceUpgrade),
        storeUrl: targetVersionInfo.storeUrl ?? '(none)',
      });

      const promptDismissible = !targetVersionInfo.forceUpgrade;
      if (promptDismissible && isAppUpdatePromptDismissed(targetVersionInfo)) {
        clearPendingAppUpdate();
        logger.debug('[native-update] skipping dismissed native update prompt', {
          reason,
          currentVersion: currentAppVersion.displayVersion,
          targetVersion,
          storeUrl: targetVersionInfo.storeUrl ?? '(none)',
        });
        return;
      }

      const dismissUpdatePrompt = (): void => {
        dismissAppUpdatePrompt(targetVersionInfo);
        clearPendingAppUpdate();
        logger.info('[native-update] dismissed native update prompt', {
          currentVersion: currentAppVersion.displayVersion,
          targetVersion,
          storeUrl: targetVersionInfo.storeUrl ?? '(none)',
        });
      };

      const updateAction = async (): Promise<void> => {
        const opened = await openNativeStoreForUpdate(targetVersionInfo, 'manual-action');
        if (!opened) {
          logger.warn('Native store update action did not open a store listing', {
            currentVersion: currentAppVersion.displayVersion,
            targetVersion,
            platform: getAppPlatform(),
            storeUrl: targetVersionInfo.storeUrl ?? '(none)',
          });
        }
      };

      setSnackbarMessage({
        text: promptText,
        duration: null,
        actionLabel: 'Update',
        onAction: updateAction,
        dismissible: promptDismissible,
        dismissLabel: 'Dismiss update prompt',
        onDismiss: promptDismissible ? dismissUpdatePrompt : undefined,
      });

      if (!autoOpen) {
        return;
      }

      const opened = await openNativeStoreForUpdate(targetVersionInfo, 'automatic-force-upgrade');
      if (!opened) {
        setSnackbarMessage({
          text: failedAutoOpenText,
          duration: null,
          actionLabel: 'Update',
          onAction: updateAction,
          dismissible: promptDismissible,
          dismissLabel: 'Dismiss update prompt',
          onDismiss: promptDismissible ? dismissUpdatePrompt : undefined,
        });
      }
    },
    [openNativeStoreForUpdate],
  );

  const reconcileCurrentAppVersion = useCallback(() => {
    const misc = getMisc();
    const storedVersion = getAppVersionInfoFromMisc(misc);
    const pendingResolution = resolvePendingAppUpdate(currentAppVersion);

    logger.info('Reconciling current app version state', {
      storedVersion: storedVersion?.displayVersion ?? '(none)',
      currentVersion: currentAppVersion.displayVersion,
      pendingStatus: pendingResolution.status,
      pendingTarget: pendingResolution.pendingUpdate?.target.displayVersion ?? '(none)',
      pendingAttempts: pendingResolution.pendingUpdate?.attempts ?? 0,
    });

    persistCurrentAppVersion();

    if (pendingResolution.status === 'updated' && pendingResolution.pendingUpdate) {
      setSnackbarMessage({
        text: `Updated from ${formatAppVersionLabel(pendingResolution.pendingUpdate.from)} to ${formatAppVersionLabel(currentAppVersion)}.`,
        duration: 6000,
      });
      return;
    }

    if (
      pendingResolution.status === 'waiting' &&
      pendingResolution.pendingUpdate &&
      isNativeApp() &&
      pendingResolution.pendingUpdate.comparisonScope === 'full'
    ) {
      if (!pendingResolution.pendingUpdate.forceUpgrade && !pendingResolution.pendingUpdate.storeUpdatePromptsEnabled) {
        clearPendingAppUpdate();
        logger.info('[native-update] cleared pending native update prompt because prompts are disabled', {
          currentVersion: currentAppVersion.displayVersion,
          targetVersion: pendingResolution.pendingUpdate.target.displayVersion,
          attempts: pendingResolution.pendingUpdate.attempts,
          forceUpgrade: Boolean(pendingResolution.pendingUpdate.forceUpgrade),
          storeUpdatePromptsEnabled: Boolean(pendingResolution.pendingUpdate.storeUpdatePromptsEnabled),
          storeUrl: pendingResolution.pendingUpdate.storeUrl ?? '(none)',
        });
        return;
      }

      logger.debug('[native-update] restoring pending prompt during version reconciliation', {
        currentVersion: currentAppVersion.displayVersion,
        targetVersion: pendingResolution.pendingUpdate.target.displayVersion,
        attempts: pendingResolution.pendingUpdate.attempts,
        forceUpgrade: Boolean(pendingResolution.pendingUpdate.forceUpgrade),
        storeUrl: pendingResolution.pendingUpdate.storeUrl ?? '(none)',
      });
      void presentNativeUpdatePrompt(
        {
          ...pendingResolution.pendingUpdate.target,
          forceUpgrade: pendingResolution.pendingUpdate.forceUpgrade,
          storeUrl: pendingResolution.pendingUpdate.storeUrl ?? null,
          storeUpdatePromptsEnabled: pendingResolution.pendingUpdate.storeUpdatePromptsEnabled,
        },
        {
          autoOpen: false,
          reason: 'startup-restore',
        },
      );
      return;
    }

    if (storedVersion && compareAppReleaseVersion(storedVersion, currentAppVersion) < 0) {
      setSnackbarMessage({
        text: `Updated from ${formatAppVersionLabel(storedVersion)} to ${formatAppVersionLabel(currentAppVersion)}.`,
        duration: 6000,
      });
      return;
    }

    if (storedVersion && compareAppReleaseVersion(storedVersion, currentAppVersion) > 0) {
      logger.warn('Stored app version is newer than the current build', {
        storedVersion: storedVersion.displayVersion,
        currentVersion: currentAppVersion.displayVersion,
      });
    }
  }, [persistCurrentAppVersion, presentNativeUpdatePrompt]);

  useEffect(() => {
    if (!localForageLoaded || hasReconciledVersionStateRef.current) {
      return;
    }

    hasReconciledVersionStateRef.current = true;
    reconcileCurrentAppVersion();
  }, [localForageLoaded, reconcileCurrentAppVersion]);

  const scheduleUpdateReload = useCallback((targetVersion: string, attempts: number, serviceWorkerUpdated: boolean) => {
    if (reloadTimeoutRef.current !== null) {
      window.clearTimeout(reloadTimeoutRef.current);
    }

    const reloadDelayMs = serviceWorkerUpdated ? 4000 : 2000;
    reloadTimeoutRef.current = window.setTimeout(() => {
      logger.warn('Falling back to page reload to apply app update', {
        currentVersion: currentAppVersion.displayVersion,
        targetVersion,
        attempts,
        reloadDelayMs,
      });
      window.location.reload();
    }, reloadDelayMs);
  }, []);

  const startAppUpdate = useCallback(
    async (targetVersionInfo: NativeUpdateTarget) => {
      if (isNativeApp()) {
        const nativePlatform = getAppPlatform();
        if (!shouldPresentNativeStoreUpdatePrompt(targetVersionInfo)) {
          clearPendingAppUpdate();
          logger.info('[native-update] newer native build found but store update prompt is disabled', {
            currentVersion: currentAppVersion.displayVersion,
            targetVersion: targetVersionInfo.displayVersion,
            platform: nativePlatform,
            forceUpgrade: Boolean(targetVersionInfo.forceUpgrade),
            storeUpdatePromptsEnabled: Boolean(targetVersionInfo.storeUpdatePromptsEnabled),
            storeUrl: targetVersionInfo.storeUrl ?? '(none)',
          });
          return;
        }

        const pendingUpdate = beginPendingAppUpdate(currentAppVersion, targetVersionInfo, Date.now(), {
          comparisonScope: 'full',
          forceUpgrade: Boolean(targetVersionInfo.forceUpgrade),
          storeUrl: targetVersionInfo.storeUrl ?? null,
          storeUpdatePromptsEnabled: Boolean(targetVersionInfo.storeUpdatePromptsEnabled),
        });
        logger.warn('Detected a newer native build', {
          currentVersion: currentAppVersion.displayVersion,
          targetVersion: targetVersionInfo.displayVersion,
          platform: nativePlatform,
          forceUpgrade: Boolean(targetVersionInfo.forceUpgrade),
          storeUrl: targetVersionInfo.storeUrl ?? '(none)',
        });
        logger.debug('[native-update] started pending native update flow', {
          currentVersion: currentAppVersion.displayVersion,
          targetVersion: targetVersionInfo.displayVersion,
          attempts: pendingUpdate.attempts,
          startedAt: pendingUpdate.startedAt,
          forceUpgrade: Boolean(targetVersionInfo.forceUpgrade),
          storeUpdatePromptsEnabled: Boolean(targetVersionInfo.storeUpdatePromptsEnabled),
          storeUrl: targetVersionInfo.storeUrl ?? '(none)',
        });

        await presentNativeUpdatePrompt(targetVersionInfo, {
          autoOpen: Boolean(targetVersionInfo.forceUpgrade),
          reason: 'fresh-check',
        });

        return;
      }

      const pendingUpdate = beginPendingAppUpdate(currentAppVersion, targetVersionInfo);

      logger.warn('Detected a newer app build and started update flow', {
        currentVersion: currentAppVersion.displayVersion,
        targetVersion: targetVersionInfo.displayVersion,
        attempts: pendingUpdate.attempts,
        startedAt: pendingUpdate.startedAt,
      });

      if (pendingUpdate.attempts > MAX_APP_UPDATE_AUTO_RELOAD_ATTEMPTS) {
        setSnackbarMessage({
          text: `${formatAppVersionLabel(targetVersionInfo)} is ready, but automatic reloading has already been attempted several times. Please refresh again in a moment if it still does not appear.`,
          duration: 10000,
        });
        logger.error('Aborting automatic reload after repeated failed update attempts', pendingUpdate);
        return;
      }

      setSnackbarMessage({
        text: `Updating to ${formatAppVersionLabel(targetVersionInfo)}...`,
        duration: null,
      });

      const serviceWorkerUpdated = await requestServiceWorkerUpdate();
      logger.info('Service worker update request completed', {
        targetVersion: targetVersionInfo.displayVersion,
        serviceWorkerUpdated,
        attempts: pendingUpdate.attempts,
      });

      scheduleUpdateReload(targetVersionInfo.displayVersion, pendingUpdate.attempts, serviceWorkerUpdated);
    },
    [scheduleUpdateReload],
  );

  const checkAppVersion = useCallback(async () => {
    if (!localForageLoaded) {
      return;
    }

    if (versionCheckInFlightRef.current) {
      logger.debug('Skipping app version check because another version check is already running');
      return;
    }

    const currentTime = Date.now();
    const misc = getMisc();
    const lastCheckTime = misc?.lastVersionCheck || 0;
    const pendingUpdate = getPendingAppUpdate(currentTime);
    const nativeApp = isNativeApp();
    const minimumCheckIntervalMs = pendingUpdate
      ? PENDING_UPDATE_CHECK_INTERVAL_MS
      : nativeApp
        ? NATIVE_VERSION_CHECK_INTERVAL_MS
        : DEFAULT_VERSION_CHECK_INTERVAL_MS;

    if (currentTime - lastCheckTime < minimumCheckIntervalMs) {
      logger.debug('Skipping app version check because the minimum interval has not elapsed', {
        currentVersion: currentAppVersion.displayVersion,
        lastCheckTime,
        minimumCheckIntervalMs,
        pendingTarget: pendingUpdate?.target.displayVersion ?? '(none)',
      });
      return;
    }

    versionCheckInFlightRef.current = true;
    logger.debug('Checking for app version update', {
      currentVersion: currentAppVersion.displayVersion,
      lastCheckTime,
      pendingTarget: pendingUpdate?.target.displayVersion ?? '(none)',
      isOnline,
    });

    try {
      const remoteVersionInfo = await checkVersion();
      updateMisc({
        ...withAppVersionInMisc(getMisc(), currentAppVersion),
        lastVersionCheck: currentTime,
      });

      if (!remoteVersionInfo) {
        logger.debug('[app-version-check] remote version metadata was empty after checkVersion()', {
          currentVersion: currentAppVersion.displayVersion,
        });
        logger.warn('App version check finished without remote version metadata');
        return;
      }

      const versionComparison = compareAppVersionInfo(currentAppVersion, remoteVersionInfo);
      const releaseComparison = compareAppReleaseVersion(currentAppVersion, remoteVersionInfo);
      const nativeComparison = nativeApp ? compareNativeAppVersionInfo(currentAppVersion, remoteVersionInfo) : null;
      const shouldPromptForUpdate = nativeApp ? nativeComparison !== null && nativeComparison < 0 : releaseComparison < 0;
      logger.debug('[app-version-check] evaluated remote version metadata', {
        currentVersion: currentAppVersion.displayVersion,
        remoteVersion: remoteVersionInfo.displayVersion,
        nativeApp,
        versionComparison,
        releaseComparison,
        nativeComparison,
        shouldPromptForUpdate,
        forceUpgrade: Boolean(remoteVersionInfo.forceUpgrade),
        storeUpdatePromptsEnabled: Boolean(remoteVersionInfo.storeUpdatePromptsEnabled),
        storeUrl: remoteVersionInfo.storeUrl ?? '(none)',
      });
      logger.info('Received app version metadata from server', {
        currentVersion: currentAppVersion.displayVersion,
        remoteVersion: remoteVersionInfo.displayVersion,
        versionComparison,
        releaseComparison,
        nativeComparison,
        shouldPromptForUpdate,
        forceUpgrade: Boolean(remoteVersionInfo.forceUpgrade),
        storeUpdatePromptsEnabled: Boolean(remoteVersionInfo.storeUpdatePromptsEnabled),
        storeUrl: remoteVersionInfo.storeUrl ?? '(none)',
      });

      if (shouldPromptForUpdate) {
        if (nativeApp && !remoteVersionInfo.forceUpgrade && isAppUpdatePromptDismissed(remoteVersionInfo)) {
          clearPendingAppUpdate();
          logger.debug('[native-update] suppressing dismissed native update prompt during version check', {
            currentVersion: currentAppVersion.displayVersion,
            remoteVersion: remoteVersionInfo.displayVersion,
            storeUrl: remoteVersionInfo.storeUrl ?? '(none)',
          });
          return;
        }

        await startAppUpdate(remoteVersionInfo);
        return;
      }

      if (nativeApp && pendingUpdate?.comparisonScope === 'full') {
        clearPendingAppUpdate();
        setSnackbarMessage(null);
        logger.info('[native-update] cleared stale pending native update after server reported no update', {
          currentVersion: currentAppVersion.displayVersion,
          remoteVersion: remoteVersionInfo.displayVersion,
          pendingTarget: pendingUpdate.target.displayVersion,
          forceUpgrade: Boolean(remoteVersionInfo.forceUpgrade),
          storeUpdatePromptsEnabled: Boolean(remoteVersionInfo.storeUpdatePromptsEnabled),
          storeUrl: remoteVersionInfo.storeUrl ?? '(none)',
        });
      }

      if (nativeApp ? nativeComparison !== null && nativeComparison > 0 : releaseComparison > 0) {
        logger.warn('Server reported an older app build than the current client build', {
          currentVersion: currentAppVersion.displayVersion,
          remoteVersion: remoteVersionInfo.displayVersion,
          versionComparison,
          releaseComparison,
          nativeComparison,
        });
      } else if (!nativeApp && versionComparison !== 0) {
        logger.info('Ignoring build-only app version difference because release semver is unchanged', {
          currentVersion: currentAppVersion.displayVersion,
          remoteVersion: remoteVersionInfo.displayVersion,
        });
      }

      if (pendingUpdate) {
        const pendingResolution = resolvePendingAppUpdate(currentAppVersion);
        logger.debug('Checked app version while an update was still pending', {
          pendingStatus: pendingResolution.status,
          pendingTarget: pendingResolution.pendingUpdate?.target.displayVersion ?? '(none)',
          currentVersion: currentAppVersion.displayVersion,
        });
      }
    } catch (error) {
      logger.error('Failed to check app version:', error);
    } finally {
      versionCheckInFlightRef.current = false;
    }
  }, [isOnline, localForageLoaded, startAppUpdate]);

  useEffect(() => {
    if (!localForageLoaded) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      const pendingUpdate = getPendingAppUpdate();
      if (isNativeApp() && pendingUpdate?.comparisonScope === 'full') {
        if (!pendingUpdate.forceUpgrade && !pendingUpdate.storeUpdatePromptsEnabled) {
          clearPendingAppUpdate();
          logger.info('[native-update] cleared visible pending native update prompt because prompts are disabled', {
            currentVersion: currentAppVersion.displayVersion,
            targetVersion: pendingUpdate.target.displayVersion,
            attempts: pendingUpdate.attempts,
            forceUpgrade: Boolean(pendingUpdate.forceUpgrade),
            storeUpdatePromptsEnabled: Boolean(pendingUpdate.storeUpdatePromptsEnabled),
            storeUrl: pendingUpdate.storeUrl ?? '(none)',
          });
        } else {
          logger.debug('[native-update] document became visible while a native update is still pending', {
            currentVersion: currentAppVersion.displayVersion,
            targetVersion: pendingUpdate.target.displayVersion,
            attempts: pendingUpdate.attempts,
            forceUpgrade: Boolean(pendingUpdate.forceUpgrade),
            storeUpdatePromptsEnabled: Boolean(pendingUpdate.storeUpdatePromptsEnabled),
            storeUrl: pendingUpdate.storeUrl ?? '(none)',
          });
          void presentNativeUpdatePrompt(
            {
              ...pendingUpdate.target,
              forceUpgrade: pendingUpdate.forceUpgrade,
              storeUrl: pendingUpdate.storeUrl ?? null,
              storeUpdatePromptsEnabled: pendingUpdate.storeUpdatePromptsEnabled,
            },
            {
              autoOpen: false,
              reason: 'visibility-restore',
            },
          );
        }
      }

      void checkAppVersion();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkAppVersion, localForageLoaded, presentNativeUpdatePrompt]);

  //   useEffect(() => {
  //     const updateOnlineStatus = () => {
  //       if (navigator.onLine !== isOnline) {
  //         setOnline(navigator.onLine);
  //       }
  //     };

  //     // Set initial online status
  //     updateOnlineStatus();

  //     // Add event listeners for online/offline events
  //     window.addEventListener('online', updateOnlineStatus);
  //     window.addEventListener('offline', updateOnlineStatus);

  //     // Clean up event listeners on component unmount
  //     return () => {
  //       window.removeEventListener('online', updateOnlineStatus);
  //       window.removeEventListener('offline', updateOnlineStatus);
  //     };
  //   }, [isOnline, setOnline]);

  return (
    <AppContext.Provider
      value={{
        prefs,
        changePrefs,
        effectiveTheme,
        snackbarMessage,
        setSnackbarMessage,
        isOnline,
        setOnline,
        checkAppVersion,
        localForageLoaded,
        user,
        setUser,
        refreshId,
        triggerRefresh,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
